import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CalendarClock, Camera, Check, Loader2, PartyPopper } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import UniversalPhotoUploadDialog from "@/components/common/UniversalPhotoUploadDialog";
import { cn } from "@/lib/utils";
import { constructionStagesAPI } from "@/api/construction-stages";
import { progressAPI } from "@/api/progress";
import { projectsAPI } from "@/api/projects";
import { ConstructionStage } from "@/types";
import { useNavigate } from "react-router-dom";
import useSubscription from "@/contexts/SubscriptionContext";
import { canUploadImages } from "@/lib/featureGates";

interface ProgressQuickEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialProjectId?: string;
}

interface ProjectOption {
  id: string;
  name: string;
}

interface StageQueueState {
  visible: ConstructionStage[];
  queue: ConstructionStage[];
}

const VISIBLE_COUNT = 3;

const ProgressQuickEntryModal: React.FC<ProgressQuickEntryModalProps> = ({
  isOpen,
  onClose,
  initialProjectId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status: subscriptionStatus } = useSubscription();
  const tierBlocked = !canUploadImages(subscriptionStatus);

  const [projectId, setProjectId] = useState<string | undefined>(initialProjectId);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingStages, setLoadingStages] = useState(false);
  const [stageState, setStageState] = useState<StageQueueState>({ visible: [], queue: [] });
  const [leavingId, setLeavingId] = useState<string | null>(null);
  const [selectedStage, setSelectedStage] = useState<ConstructionStage | null>(null);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  // Stage the user tapped, while they're choosing photo vs. no-photo completion
  const [confirmStage, setConfirmStage] = useState<ConstructionStage | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  const translateStageField = (value?: string) =>
    value && value.startsWith("progress_tab.") ? t(value) : value || "";

  // Reset everything when the modal closes so it starts fresh next time
  useEffect(() => {
    if (isOpen) return;
    setProjectId(initialProjectId);
    setStageState({ visible: [], queue: [] });
    setLeavingId(null);
    setSelectedStage(null);
    setPhotoDialogOpen(false);
    setHasLoaded(false);
    setConfirmStage(null);
    setIsCompleting(false);
  }, [isOpen, initialProjectId]);

  // Load the project picker list when no project context is available
  useEffect(() => {
    if (!isOpen || initialProjectId) return;

    let cancelled = false;
    (async () => {
      setLoadingProjects(true);
      try {
        const data = await projectsAPI.getProjects();
        if (cancelled) return;
        setProjects(
          data
            .filter((project) => project.status !== "completed")
            .map((project) => ({ id: project.id, name: project.name }))
        );
      } catch {
        if (!cancelled) toast.error(t("progress_tab.progress_load_error"));
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, initialProjectId, t]);

  // Load the oldest pending stages for the selected project
  useEffect(() => {
    if (!isOpen || !projectId) return;

    let cancelled = false;
    (async () => {
      setLoadingStages(true);
      setHasLoaded(false);
      try {
        const { data, error } = await constructionStagesAPI.getProjectStages(projectId);
        if (error || !data) throw error || new Error("No data");
        if (cancelled) return;

        const pending = data
          .filter((stage) => !stage.completed)
          .sort(
            (a, b) =>
              (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER)
          );

        setStageState({
          visible: pending.slice(0, VISIBLE_COUNT),
          queue: pending.slice(VISIBLE_COUNT),
        });
      } catch {
        if (!cancelled) toast.error(t("progress_tab.progress_load_error"));
      } finally {
        if (!cancelled) {
          setLoadingStages(false);
          setHasLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, t]);

  // Plays the exit animation, then swaps the completed stage for the next pending one
  const finalizeStageCompletion = (stageId: string) => {
    setLeavingId(stageId);
    window.setTimeout(() => {
      setStageState((prev) => {
        const remaining = prev.visible.filter((s) => s.id !== stageId);
        const queueCopy = [...prev.queue];
        const next = queueCopy.shift();
        if (next) remaining.push(next);
        return { visible: remaining, queue: queueCopy };
      });
      setLeavingId(null);
    }, 280);
  };

  const handleCardClick = (stage: ConstructionStage) => {
    if (leavingId) return;
    setConfirmStage(stage);
  };

  const handleCancelConfirm = () => {
    setConfirmStage(null);
  };

  const handleChoosePhoto = () => {
    if (!confirmStage) return;
    if (tierBlocked) {
      toast.info(t("photo_upload.premium_required"));
      navigate("/subscription");
      return;
    }
    setSelectedStage(confirmStage);
    setPhotoDialogOpen(true);
  };

  // Photo is optional: this path completes the stage without one
  const handleCompleteWithoutPhoto = async () => {
    if (!confirmStage || !projectId) return;
    const stageId = confirmStage.id;
    setIsCompleting(true);

    try {
      const { error } = await constructionStagesAPI.updateStage(stageId, {
        completed: true,
        completedDate: new Date(),
      });
      if (error) throw error;

      await progressAPI.updateProjectProgress(projectId);
      toast.success(t("progress_tab.stage_completed"));
      setConfirmStage(null);
      finalizeStageCompletion(stageId);
    } catch {
      toast.error(t("progress_tab.stage_update_error"));
    } finally {
      setIsCompleting(false);
    }
  };

  const handlePhotoUploaded = async (photoUrl: string) => {
    if (!selectedStage || !projectId) return;
    const stageId = selectedStage.id;
    setPhotoDialogOpen(false);

    try {
      const { error } = await constructionStagesAPI.updateStage(stageId, {
        photoUrl,
        completed: true,
        completedDate: new Date(),
      });
      if (error) throw error;

      await progressAPI.updateProjectProgress(projectId);
      toast.success(t("progress_tab.stage_completed"));
      setConfirmStage(null);
      finalizeStageCompletion(stageId);
    } catch {
      toast.error(t("progress_tab.stage_update_error"));
    }
  };

  const showPicker = !initialProjectId && !projectId;
  const allDone = hasLoaded && !loadingStages && stageState.visible.length === 0 && stageState.queue.length === 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>İlerleme Kaydet</DialogTitle>
            <DialogDescription>
              {confirmStage
                ? "Bu adımı fotoğrafla ya da fotoğrafsız tamamlayabilirsiniz."
                : "Sıradaki adımlardan birine dokunup tamamlandı olarak işaretleyin."}
            </DialogDescription>
          </DialogHeader>

          {confirmStage ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    confirmStage.phaseColor || "bg-gray-100"
                  )}
                >
                  <CalendarClock className="h-4 w-4 text-white" />
                </div>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-gray-900">
                    {translateStageField(confirmStage.name)}
                  </span>
                  <span className="block truncate text-[11px] text-gray-500">
                    {translateStageField(confirmStage.phase)}
                  </span>
                </span>
              </div>

              <div className="space-y-2">
                <Button type="button" className="w-full" onClick={handleChoosePhoto} disabled={isCompleting}>
                  <Camera className="mr-2 h-4 w-4" />
                  Fotoğraf Ekle
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleCompleteWithoutPhoto}
                  disabled={isCompleting}
                >
                  {isCompleting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  Fotoğrafsız Tamamla
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleCancelConfirm}
                  disabled={isCompleting}
                >
                  Vazgeç
                </Button>
              </div>
            </div>
          ) : showPicker ? (
            <div className="space-y-2">
              <Select
                onValueChange={(value) => setProjectId(value)}
                disabled={loadingProjects}
              >
                <SelectTrigger className="bg-background">
                  {loadingProjects ? (
                    <div className="flex items-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span>Yükleniyor...</span>
                    </div>
                  ) : (
                    <SelectValue placeholder="Proje seçin" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                  {projects.length === 0 && !loadingProjects && (
                    <div className="px-4 py-2 text-sm text-muted-foreground">
                      Aktif proje bulunamadı
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : loadingStages && !hasLoaded ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              <span className="text-sm">Yükleniyor...</span>
            </div>
          ) : allDone ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <PartyPopper className="h-8 w-8 text-brand-600" />
              <p className="text-sm font-semibold text-gray-900">Tüm ilerleme adımları tamamlandı</p>
              <p className="text-xs text-muted-foreground">Bu proje için eklenecek yeni adım yok.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {stageState.visible.map((stage) => (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => handleCardClick(stage)}
                  disabled={!!leavingId}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition-all duration-[280ms] ease-out animate-in fade-in slide-in-from-bottom-2",
                    "hover:border-brand-200 hover:shadow-md",
                    leavingId === stage.id && "pointer-events-none -translate-x-4 scale-95 opacity-0"
                  )}
                >
                  {/* divs, not spans: a global CSS rule force-clears span backgrounds */}
                  <div
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                      stage.phaseColor || "bg-gray-100"
                    )}
                  >
                    <CalendarClock className="h-4 w-4 text-white" />
                  </div>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-gray-900">
                      {translateStageField(stage.name)}
                    </span>
                    <span className="block truncate text-[11px] text-gray-500">
                      {translateStageField(stage.phase)}
                    </span>
                  </span>
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                    <Camera className="h-4 w-4" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedStage && (
        <UniversalPhotoUploadDialog
          isOpen={photoDialogOpen}
          onClose={() => {
            setPhotoDialogOpen(false);
            setConfirmStage(null);
          }}
          onPhotoUploaded={handlePhotoUploaded}
          bucketName="site-photos"
          folderPath={`projects/${projectId}/stages`}
          title="Fotoğraf Ekle"
          description={`"${translateStageField(selectedStage.name)}" adımı için fotoğraf yükleyin`}
        />
      )}
    </>
  );
};

export default ProgressQuickEntryModal;
